import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Banner, Card, IndexTable, Page, Text, Thumbnail } from '@shopify/polaris';
import { ImageIcon } from '@shopify/polaris-icons';
import agent from '../../app/api/agent';
import type {
    AtdProductAttribute,
    AtdProductDetail,
    AtdProductImage,
} from '../../app/models/Atd';

const showToast = (msg: string) => window.shopify?.toast?.show?.(msg);

const formatDate = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

const formatValue = (value?: string | number | boolean | null) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
};

const formatJson = (value?: string) => {
    if (!value) return '';
    try {
        return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
        return value;
    }
};

const getProductTitle = (product: AtdProductDetail) =>
    [product.brandName, product.productName, product.size]
        .filter((value) => value && value.trim())
        .join(' - ') || product.partNumber || `ATD product ${product.id}`;

const getCatalogStatus = (product: AtdProductDetail) => {
    if (product.isDiscontinued) return { label: 'Discontinued', tone: 'critical' as const };
    if (!product.isActive) return { label: 'Inactive', tone: 'attention' as const };
    return { label: 'Active', tone: 'success' as const };
};

const buildImageList = (product: AtdProductDetail): AtdProductImage[] => {
    if (product.images.length > 0) return product.images;
    if (!product.primaryImageUrl) return [];

    return [{
        url: product.primaryImageUrl,
        thumbnailUrl: product.primaryImageUrl,
        altText: product.productName || product.partNumber,
        sortOrder: 0,
        isPrimary: true,
    }];
};

const groupAttributes = (attributes: AtdProductAttribute[]) =>
    attributes.reduce<Record<string, AtdProductAttribute[]>>((groups, attribute) => {
        const groupName = attribute.groupName?.trim() || 'Specifications';
        groups[groupName] = [...(groups[groupName] ?? []), attribute];
        return groups;
    }, {});

const AtdProductDetailsPage: React.FC = () => {
    const { id } = useParams();
    const productId = Number(id);
    const navigate = useNavigate();
    const [product, setProduct] = useState<AtdProductDetail | null>(null);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [manualLinkSaving, setManualLinkSaving] = useState(false);

    const loadProduct = useCallback(async () => {
        if (!Number.isFinite(productId) || productId <= 0) {
            setError('Invalid ATD product id.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const detail = await agent.Atd.product(productId);
            setProduct(detail);
            setSelectedImageUrl(detail.images[0]?.url || detail.primaryImageUrl || '');
        } catch (err: any) {
            setError(err?.message ?? 'Failed to load ATD product.');
        } finally {
            setLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        loadProduct();
    }, [loadProduct]);

    const saveManualLink = useCallback(async (action: 'Link' | 'Unlink') => {
        if (!product) return;

        setManualLinkSaving(true);
        setError('');
        try {
            const result = await agent.Atd.saveProductManualLinks({
                action,
                atdProductIds: [product.id],
            });

            if (result.failedCount > 0) {
                setError(result.failures[0]?.message ?? result.message);
            } else {
                showToast(result.message);
                await loadProduct();
            }
        } catch (err: any) {
            setError(err?.message ?? 'Failed to update manual ATD link.');
        } finally {
            setManualLinkSaving(false);
        }
    }, [loadProduct, product]);

    const images = useMemo(() => product ? buildImageList(product) : [], [product]);
    const attributeGroups = useMemo(() => product ? groupAttributes(product.attributes) : {}, [product]);
    const rawPayload = useMemo(() => formatJson(product?.rawPayloadJson), [product?.rawPayloadJson]);
    const title = product ? getProductTitle(product) : 'ATD Product';
    const catalogStatus = product ? getCatalogStatus(product) : null;
    const selectedImage = selectedImageUrl || images[0]?.url || '';
    const isManualLinkActive = product?.manualSyncAction === 'Link';
    const isLinkedToShopify = Boolean(product?.shopifyProductId || isManualLinkActive);
    const manualLinkAction = isLinkedToShopify ? 'Unlink' : 'Link';
    const linkStatusBadge = product
        ? isManualLinkActive && product.shopifyProductId ? (
            <Badge tone="info">Manually linked</Badge>
        ) : isManualLinkActive ? (
            <Badge tone="info">Manual link pending</Badge>
        ) : product.shopifyProductId ? (
            <Badge tone="info">Linked</Badge>
        ) : product.manualSyncAction === 'Unlink' ? (
            <Badge tone="attention">Manually unlinked</Badge>
        ) : (
            <Badge tone="attention">Unlinked</Badge>
        )
        : null;

    const sourceRows = product
        ? [
            ['ATD part #', product.partNumber],
            ['Manufacturer part #', product.manufacturerPartNumber],
            ['Alternate part #', product.alternatePartNumber],
            ['UPC', product.upc],
            ['Brand code', product.brandCode],
            ['Category', product.categoryName],
            ['Category code', product.categoryCode],
            ['Subcategory', product.subcategoryName],
            ['Subcategory code', product.subcategoryCode],
            ['Unit', product.unitOfMeasure],
            ['Weight', product.weight],
            ['Dimensions', [product.length, product.width, product.height].some((value) => value != null)
                ? `${formatValue(product.length)} x ${formatValue(product.width)} x ${formatValue(product.height)}`
                : '-'],
            ['Hazmat', product.isHazmat],
            ['Freight', product.requiresFreight],
            ['Manual sync', product.manualSyncAction ? `${product.manualSyncAction} ${formatDate(product.manualSyncAt)}` : '-'],
            ['Source updated', formatDate(product.sourceUpdatedAt)],
            ['Catalog sync', formatDate(product.lastCatalogSyncAt)],
            ['Fitment full sync', formatDate(product.lastFitmentFullSyncAt)],
            ['Missing fitment sweep', formatDate(product.lastMissingFitmentSweepAt)],
            ['Shopify sync', formatDate(product.lastShopifySyncAt)],
        ]
        : [];

    return (
        <Page
            fullWidth
            title={title}
            titleMetadata={(catalogStatus || linkStatusBadge) ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {catalogStatus && <Badge tone={catalogStatus.tone}>{catalogStatus.label}</Badge>}
                    {linkStatusBadge}
                </span>
            ) : undefined}
            backAction={{ content: 'Products', onAction: () => navigate('/products?tab=atd') }}
            secondaryActions={product ? [
                {
                    content: manualLinkAction,
                    onAction: () => saveManualLink(manualLinkAction),
                    loading: manualLinkSaving,
                    disabled: manualLinkSaving || (manualLinkAction === 'Link' && (!product.isActive || product.isDiscontinued)),
                    destructive: manualLinkAction === 'Unlink',
                },
            ] : undefined}
            primaryAction={{
                content: 'Refresh',
                onAction: loadProduct,
                loading,
            }}
        >
            {error && (
                <div style={{ marginBottom: 12 }}>
                    <Banner title="Error" tone="critical">{error}</Banner>
                </div>
            )}

            <Card>
                {product ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 420px) minmax(0, 1fr)', gap: 24, alignItems: 'start' }}>
                        <div style={{ display: 'grid', gap: 12 }}>
                            <div style={{
                                border: '1px solid #e3e3e3',
                                borderRadius: 8,
                                minHeight: 360,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#fff',
                            }}>
                                {selectedImage ? (
                                    <img
                                        src={selectedImage}
                                        alt={title}
                                        style={{ maxWidth: '100%', maxHeight: 360, objectFit: 'contain' }}
                                    />
                                ) : (
                                    <Thumbnail source={ImageIcon} alt={title} size="large" />
                                )}
                            </div>
                            {images.length > 1 && (
                                <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                                    {images.map((image, index) => {
                                        const source = image.thumbnailUrl || image.url || '';
                                        if (!source) return null;

                                        return (
                                            <button
                                                key={`${source}-${index}`}
                                                type="button"
                                                onClick={() => setSelectedImageUrl(image.url || source)}
                                                style={{
                                                    width: 72,
                                                    height: 72,
                                                    border: selectedImage === image.url ? '2px solid #303030' : '1px solid #d0d5dd',
                                                    borderRadius: 6,
                                                    background: '#fff',
                                                    padding: 4,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <img src={source} alt={image.altText || title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'grid', gap: 18 }}>
                            {(product.isHazmat || product.requiresFreight) && (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {product.isHazmat && <Badge tone="warning">Hazmat</Badge>}
                                {product.requiresFreight && <Badge tone="warning">Freight</Badge>}
                                </div>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                                <div>
                                    <Text as="p" variant="bodySm" tone="subdued">Part #</Text>
                                    <Text as="p" variant="headingSm">{product.partNumber || '-'}</Text>
                                </div>
                                <div>
                                    <Text as="p" variant="bodySm" tone="subdued">Brand</Text>
                                    <Text as="p" variant="headingSm">{product.brandName || '-'}</Text>
                                </div>
                                <div>
                                    <Text as="p" variant="bodySm" tone="subdued">Size</Text>
                                    <Text as="p" variant="headingSm">{product.size || '-'}</Text>
                                </div>
                                <div>
                                    <Text as="p" variant="bodySm" tone="subdued">Available</Text>
                                    <Text as="p" variant="headingSm">{product.availableQuantity ?? 0}</Text>
                                </div>
                            </div>

                            {product.shortDescription && (
                                <Text as="p" variant="bodyMd">{product.shortDescription}</Text>
                            )}
                            {product.longDescription && product.longDescription !== product.shortDescription && (
                                <Text as="p" variant="bodyMd" tone="subdued">{product.longDescription}</Text>
                            )}

                            <div style={{ borderTop: '1px solid #e3e3e3', paddingTop: 16, display: 'grid', gap: 12 }}>
                                <Text as="h3" variant="headingMd">Source Data</Text>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px 24px' }}>
                                    {sourceRows.map(([label, value]) => (
                                        <div key={String(label)}>
                                            <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
                                            <Text as="p" variant="bodyMd">{formatValue(value as any)}</Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <Text as="p" variant="bodyMd" tone="subdued">
                        {loading ? 'Loading ATD product...' : 'ATD product not found.'}
                    </Text>
                )}
            </Card>

            {product && (
                <>
                    <div style={{ marginTop: 16 }}>
                        <Card>
                            <div style={{ display: 'grid', gap: 16 }}>
                                <Text as="h3" variant="headingMd">Attributes</Text>
                                {Object.keys(attributeGroups).length > 0 ? (
                                    Object.entries(attributeGroups).map(([groupName, attributes]) => (
                                        <div key={groupName} style={{ display: 'grid', gap: 10 }}>
                                            <Text as="h4" variant="headingSm">{groupName}</Text>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px 24px' }}>
                                                {attributes.map((attribute, index) => (
                                                    <div key={`${groupName}-${attribute.name}-${index}`}>
                                                        <Text as="p" variant="bodySm" tone="subdued">{attribute.name || '-'}</Text>
                                                        <Text as="p" variant="bodyMd">{attribute.value || '-'}</Text>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <Text as="p" variant="bodyMd" tone="subdued">No attributes.</Text>
                                )}
                            </div>
                        </Card>
                    </div>

                    <div style={{ marginTop: 16 }}>
                        <Card>
                            <div style={{ display: 'grid', gap: 12 }}>
                                <Text as="h3" variant="headingMd">Inventory</Text>
                                {product.inventory.length > 0 ? (
                                    <IndexTable
                                        selectable={false}
                                        resourceName={{ singular: 'inventory row', plural: 'inventory rows' }}
                                        itemCount={product.inventory.length}
                                        headings={[
                                            { title: 'Location' },
                                            { title: 'Available' },
                                            { title: 'On hand' },
                                            { title: 'Backorder' },
                                            { title: 'ETA' },
                                        ]}
                                    >
                                        {product.inventory.map((inventory, index) => (
                                            <IndexTable.Row id={inventory.locationCode || String(index)} key={`${inventory.locationCode}-${index}`} position={index}>
                                                <IndexTable.Cell>{inventory.locationName || inventory.locationCode || '-'}</IndexTable.Cell>
                                                <IndexTable.Cell>{inventory.availableQuantity ?? 0}</IndexTable.Cell>
                                                <IndexTable.Cell>{inventory.onHandQuantity ?? '-'}</IndexTable.Cell>
                                                <IndexTable.Cell>{inventory.backorderQuantity ?? '-'}</IndexTable.Cell>
                                                <IndexTable.Cell>{formatDate(inventory.etaDate)}</IndexTable.Cell>
                                            </IndexTable.Row>
                                        ))}
                                    </IndexTable>
                                ) : (
                                    <Text as="p" variant="bodyMd" tone="subdued">No inventory rows.</Text>
                                )}
                            </div>
                        </Card>
                    </div>

                    <div style={{ marginTop: 16 }}>
                        <Card>
                            <div style={{ display: 'grid', gap: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                                    <Text as="h3" variant="headingMd">Fitments</Text>
                                    <Badge tone={product.fitmentCount > 0 ? 'info' : 'attention'}>{`${product.fitmentCount} total`}</Badge>
                                </div>
                                {product.fitments.length > 0 ? (
                                    <IndexTable
                                        selectable={false}
                                        resourceName={{ singular: 'fitment', plural: 'fitments' }}
                                        itemCount={product.fitments.length}
                                        headings={[
                                            { title: 'Year' },
                                            { title: 'Make' },
                                            { title: 'Model' },
                                            { title: 'Trim' },
                                            { title: 'Position' },
                                            { title: 'Group' },
                                        ]}
                                    >
                                        {product.fitments.map((fitment, index) => (
                                            <IndexTable.Row id={`${fitment.vehicleId}-${index}`} key={`${fitment.vehicleId}-${index}`} position={index}>
                                                <IndexTable.Cell>{fitment.year || '-'}</IndexTable.Cell>
                                                <IndexTable.Cell>{fitment.make || '-'}</IndexTable.Cell>
                                                <IndexTable.Cell>{fitment.model || '-'}</IndexTable.Cell>
                                                <IndexTable.Cell>{fitment.trim || fitment.trimOption || '-'}</IndexTable.Cell>
                                                <IndexTable.Cell>{fitment.position || '-'}</IndexTable.Cell>
                                                <IndexTable.Cell>{fitment.productGroup || '-'}</IndexTable.Cell>
                                            </IndexTable.Row>
                                        ))}
                                    </IndexTable>
                                ) : (
                                    <Text as="p" variant="bodyMd" tone="subdued">No fitments.</Text>
                                )}
                            </div>
                        </Card>
                    </div>

                    {rawPayload && (
                        <div style={{ marginTop: 16 }}>
                            <Card>
                                <details>
                                    <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Raw ATD Payload</summary>
                                    <pre style={{ marginTop: 12, whiteSpace: 'pre-wrap', overflowX: 'auto', maxHeight: 420 }}>
                                        {rawPayload}
                                    </pre>
                                </details>
                            </Card>
                        </div>
                    )}
                </>
            )}
        </Page>
    );
};

export default AtdProductDetailsPage;
